import { Accordion } from "components/Accordion/Accordion";
import { Button } from "components/Button/Button";
import {
    composeValidators,
    MAX_LENGTH,
    maxLengthValidator,
    oneLineValidator,
    requiredValidator,
} from "components/Form/Form.utils";
import { Form } from "components/Form/components/Form/Form";
import { TextareaFormComponent } from "components/Form/components/Textarea/TextareaFormComponent";
import { IconCollection } from "components/Icon/IconCollection";
import { deleteNestedForm } from "components/ObjectTemplateCanvas/ObjectTemplateCard";
import objectTemplateAttributeConfig from "config/resources/object_template_attribute";
import { ResourceEditFields } from "containers/Resources/components/ResourceEditFields/ResourceEditFields";
import {
    filterObjectTemplateDataTypeList,
    newRecordId,
    setValueDependingOnDataType,
    storeFormDataForCanvas,
    submitRelationshipRecordForm,
} from "containers/Resources/screens/ResourcesSingle/utils";
import { Object_Template_Attribute } from "graphql/generated/graphql";
import { useEffect, useMemo, useRef, useState } from "react";
import { Field, Form as FinalForm, FormSpy } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { useDispatch } from "react-redux";
import { useMount } from "react-use";
import styles from "./ObjectTemplateCard.module.scss";

interface AttributeCardProps {
    data?: Object_Template_Attribute | null;
    objectTemplateId?: string;
    deleteAttribute?: (id: string) => any;
    deleteNewForm: () => void;
    isDisabled: boolean;
    isReadOnly: boolean;
    setNestedAttributeFormsData: (data: any) => void;
}

/**
 * AttributeCard component that renders a form for creating or editing object template attributes.
 *
 * This component handles:
 * - Form rendering and validation for object template attributes
 * - Accordion-based expandable interface for attribute editing
 * - Automatic field focus management based on data type selection
 * - Nested forms data management for canvas integration
 * - Conditional rendering based on attribute state (new vs existing)
 * - Form state persistence and dirty tracking
 *
 * The component provides an intuitive interface for attribute creation with smart
 * field focus management that guides users through the required fields based on
 * their data type selection.
 *
 * @param data - The attribute data to display and edit
 * @param objectTemplateId - The ID of the parent object template
 * @param deleteAttribute - Function to delete an existing attribute
 * @param deleteNewForm - Function to delete the new attribute form
 * @param isDisabled - Whether the form should be disabled
 * @param isReadOnly - Whether the form should be read-only
 * @param setNestedAttributeFormsData - Function to set nested attribute forms data
 * @returns JSX.Element - The rendered attribute card form
 */
export const AttributeCard = ({
    data,
    objectTemplateId,
    deleteAttribute,
    deleteNewForm,
    isDisabled,
    isReadOnly,
    setNestedAttributeFormsData,
}: AttributeCardProps) => {
    const dispatch = useDispatch();

    const [isExpanded, setExpanded] = useState(data?.id ? false : true);
    const onExpandChange = (expanded) => setExpanded(expanded);

    // Configure accordion props based on whether this is a new or existing attribute
    const accordionProps = useMemo(
        () =>
            data?.id
                ? {
                      // Existing attribute: collapsible with custom styling
                      className: `attributeCardAccordion ${
                          isExpanded ? "" : "accordion-hideChildren"
                      }`,
                      isExpanded: isExpanded,
                      onExpandedChange: onExpandChange,
                      expandOnIconClick: isExpanded,
                  }
                : {
                      // New attribute: always expanded and non-collapsible
                      isExpanded: true,
                      onExpandedChange: () => {},
                      isExpandedByDefault: true,
                  },
        [data?.id, isExpanded]
    );

    // Clean up nested forms data when component unmounts
    useEffect(() => {
        return () => {
            setNestedAttributeFormsData((prevData) => deleteNestedForm(prevData, data?.id));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filter form structure to only show fields that should be displayed in canvas
    const recourseFormStructure = objectTemplateAttributeConfig.form?.filter(
        (field) => field.isShownInCanvas !== false
    );

    // Prepare initial values for the form
    const initialValues = useMemo(() => {
        return { ...(data ?? {}) };
    }, [data]);

    // Refs for DOM manipulation and focus management
    const inputRef = useRef<any>();
    const containerRef = useRef<any>();

    // Auto-click datatype dropdown for new attributes to guide user input
    useMount(() => {
        if (!data?.id && containerRef.current) {
            const dataTypeIcon = containerRef.current.querySelector(
                '.react-select__dropdown-icon [data-id="datatype"]'
            );
            setTimeout(() => {
                dataTypeIcon?.click();
            }, 100);
        }
    });

    /**
     * Set focus to the name field when a datatype is selected for new attributes
     * @param currentValue - The current field value
     * @param nameValue - The current name field value
     */
    const setNameFieldFocus = (currentValue, nameValue) => {
        if (!data?.id && containerRef.current && currentValue && !nameValue) {
            const textArea = inputRef.current?.resizableTextArea?.textArea;
            if (textArea) {
                textArea.focus();
                textArea.setSelectionRange(textArea.value?.length, textArea.value?.length);
            }
        }
    };

    /**
     * Click on the next required field dropdown based on datatype selection
     * @param requiredFieldName - The name of the required field to click
     */
    const clickNextRequiredField = (requiredFieldName) => {
        const requiredFieldIcon = containerRef.current.querySelector(
            `.react-select__dropdown-icon [data-id=${requiredFieldName}]`
        );
        setTimeout(() => {
            requiredFieldIcon?.click();
        }, 100);
    };

    return (
        <FinalForm
            onSubmit={(formData) =>
                submitRelationshipRecordForm({
                    dispatch,
                    formData,
                    recordId: data?.id,
                    resourceName: "object_template_attribute",
                    additionalData: {
                        parentResourceType: "object_template",
                        parentResourceId: objectTemplateId,
                    },
                    resourceConfig: objectTemplateAttributeConfig,
                    newRecordId: "",
                })?.then(() => deleteNewForm())
            }
            initialValues={initialValues}
            keepDirtyOnReinitialize
        >
            {({ handleSubmit, values, form }) => {
                // Update form values based on datatype and filter available options
                setValueDependingOnDataType(values);
                filterObjectTemplateDataTypeList(values?.id ?? "new");

                return (
                    <Form onSubmit={handleSubmit}>
                        <div className={styles.attributeContainer} ref={containerRef}>
                            <Accordion
                                title={
                                    isExpanded ? (
                                        /* Expanded view: show editable name field */
                                        <div className={styles.attributeHeader}>
                                            <div
                                                className={`${styles.nameFieldContainer} ${styles.attributeNameFieldContainer}`}
                                            >
                                                <Field
                                                    name="name"
                                                    component={TextareaFormComponent}
                                                    fieldAttr={{
                                                        required: true,
                                                        placeholder: "Name...",
                                                        disabled: isDisabled,
                                                    }}
                                                    validate={composeValidators(
                                                        requiredValidator,
                                                        maxLengthValidator(MAX_LENGTH),
                                                        oneLineValidator
                                                    )}
                                                    inputRef={inputRef}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        /* Collapsed view: show read-only name */
                                        <span className={styles.attributeName}>{values?.name}</span>
                                    )
                                }
                                className="attributeCardAccordion"
                                isDisabled={isDisabled}
                                actionButton={
                                    /* Delete button for removing the attribute */
                                    <Button
                                        icon={IconCollection.deleteOutline}
                                        variation="icon"
                                        onClick={
                                            data?.id
                                                ? () => deleteAttribute?.(data.id)
                                                : () => deleteNewForm()
                                        }
                                        disabled={isDisabled}
                                    />
                                }
                                {...accordionProps}
                            >
                                {/* Attribute form fields */}
                                <div className={styles.attributeFields}>
                                    <ResourceEditFields
                                        formStructure={recourseFormStructure}
                                        isDisabled={isDisabled}
                                        isReadOnly={isReadOnly}
                                        resourceTypeName={"object_template_attribute"}
                                        recordId={data?.id ?? ""}
                                        writeOnlyFieldsWithData={{}}
                                        additionalFieldProps={{ renderIconOnly: true }}
                                        currentValues={values}
                                    />
                                </div>
                            </Accordion>
                        </div>

                        {/* Form spy to track form state changes for canvas integration */}
                        <FormSpy
                            onChange={({ pristine }) => {
                                storeFormDataForCanvas({
                                    pristine,
                                    setNestedFormSaveData: setNestedAttributeFormsData,
                                    recordId: data?.id,
                                    form,
                                    isNew: data?.id === undefined,
                                    newRecordId,
                                });
                            }}
                            key="pristineSpy"
                        />

                        {/* Listen for datatype changes to guide user through required fields */}
                        <OnChange name="datatype">
                            {(value) => {
                                if (!data?.id && containerRef.current) {
                                    if (value === "OBJECT_TEMPLATE_REFERENCE") {
                                        // For object template references, click the reference field
                                        clickNextRequiredField("reference_object_template_id");
                                    } else if (value === "RECORD_REFERENCE") {
                                        // For record references, click the record type field
                                        clickNextRequiredField("reference_record_type");
                                    } else if (value === "ALLOWED_VALUES") {
                                        // For allowed-values dropdowns, click the options field
                                        clickNextRequiredField("allowed_values");
                                    } else if (!values.name) {
                                        // For other datatypes, focus the name field if empty
                                        setNameFieldFocus(value, values.name);
                                    }
                                }
                            }}
                        </OnChange>

                        {/* Listen for reference object template changes to focus name field */}
                        <OnChange name="reference_object_template_id">
                            {(value) => setNameFieldFocus(value, values.name)}
                        </OnChange>

                        {/* Listen for reference record type changes to focus name field */}
                        <OnChange name="reference_record_type">
                            {(value) => setNameFieldFocus(value, values.name)}
                        </OnChange>
                    </Form>
                );
            }}
        </FinalForm>
    );
};
